/**
 * The taxes behind a bill's payment, as the payment screens receive them:
 * the GST the bill was billed with (M13 t150/t155) and the TDS deducted on it
 * (M15 t176, or worked out for a voucher still being raised).
 */
export interface BillGst {
  readonly taxable: number;
  readonly cgst: number;
  readonly sgst: number;
  readonly igst: number;
  readonly cess: number;
  readonly other: number;
  /** All GST on the bill, forward and reverse charge. */
  readonly total: number;
  /** Paid by the company to the government, never to the vendor. */
  readonly reverseCharge: number;
  /** Charged by the vendor and paid to it with the bill. */
  readonly charged: number;
  /** Taxable value plus the GST the vendor charged. */
  readonly billAmount: number;
}

/** The figures a TDS deduction is judged on. */
export interface TdsBasis {
  readonly sectionCode: string;
  readonly applicableRatePct: number;
  /** The bill's taxable value. */
  readonly taxable: number;
  /** Credited to the vendor in the financial year before this bill. */
  readonly priorAggregate: number;
  readonly singlePaymentThreshold: number | null;
  readonly annualAggregateThreshold: number | null;
  readonly certificate: { readonly certificateNo: string; readonly ratePct: number } | null;
  readonly tdsAmount: number;
}

/** GET /v1/payments/advice/:adviceId/taxes */
export interface PaymentTaxes {
  readonly gst: BillGst;
  /** Null when no voucher was raised for the bill. */
  readonly tds: (TdsBasis & { readonly panStatusCode: string }) | null;
}

/** The GST components a bill can carry, in the order they are shown. */
export const GST_PARTS = ['cgst', 'sgst', 'igst', 'cess', 'other'] as const;
